import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import AuthModal from "./AuthModal";
import { Trash2, Calculator, FolderOpen, ArrowRight, FileText, RotateCcw } from "lucide-react";

import { API } from "../config";

export default function MyProjectsPage() {
  const { t, lang } = useLanguage();
  const { user, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState("");

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    axios.get(`${API}/projects`, { headers: authHeaders })
      .then(r => setProjects(r.data || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [user, authHeaders]);

  const deleteProject = async (id) => {
    if (!window.confirm(t("projects.deleteConfirm"))) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { headers: authHeaders });
      setProjects(prev => prev.filter(p => p.id !== id));
      setDeleteMsg(t("projects.deleted"));
      setTimeout(() => setDeleteMsg(""), 2500);
    } catch {
      setDeleteMsg(t("common.error"));
      setTimeout(() => setDeleteMsg(""), 2500);
    }
  };

  const loadProject = (project) => {
    navigate("/kalkulators", { state: { loadProject: project } });
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "lv" ? "lv-LV" : lang === "ru" ? "ru-RU" : "en-GB", {
        day: "numeric", month: "short", year: "numeric"
      });
    } catch { return iso; }
  };

  return (
    <div style={{ paddingTop: 72 }} className="min-h-screen bg-[#f9f9f9]">
      <section className="bg-black py-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <span className="rmd-label text-[#FF6600] block mb-2">{t("projects.title")}</span>
          <h1 className="text-white text-4xl md:text-6xl font-black uppercase tracking-tight leading-none" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="projects-title">
            {t("projects.title")}
          </h1>
          <p className="text-white/50 mt-3 text-sm">{t("projects.subtitle")}</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 md:px-12 py-8">
        {!user ? (
          <div className="bg-white border-2 border-black p-12 text-center" style={{ boxShadow: "4px 4px 0 #000" }}>
            <FolderOpen size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="font-bold text-lg mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>{t("projects.loginRequired")}</p>
            <button data-testid="projects-login-btn" onClick={() => setShowAuth(true)} className="rmd-btn-primary mt-4">
              {t("nav.login")}
            </button>
          </div>
        ) : loading ? (
          <div className="text-center py-16 text-gray-400"><p>{t("common.loading")}</p></div>
        ) : projects.length === 0 ? (
          <div className="bg-white border-2 border-black p-12 text-center" style={{ boxShadow: "4px 4px 0 #000" }}>
            <FolderOpen size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="font-bold text-lg mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>{t("projects.empty")}</p>
            <p className="text-sm text-gray-500 mb-6">{t("projects.emptyDesc")}</p>
            <Link to="/kalkulators" data-testid="projects-go-calc" className="rmd-btn-primary">
              <Calculator size={16} /> {t("projects.goCalc")} <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {deleteMsg && (
              <div className={`text-sm px-4 py-2 border ${deleteMsg.includes(t("common.error")) ? "text-red-600 bg-red-50 border-red-200" : "text-green-600 bg-green-50 border-green-200"}`}>
                {deleteMsg}
              </div>
            )}
            {projects.map(p => (
              <div key={p.id} data-testid={`project-card-${p.id}`}
                className="bg-white border-2 border-black p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                style={{ boxShadow: "3px 3px 0 #000" }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText size={18} className="text-[#FF6600] flex-shrink-0" />
                    <h3 className="font-black text-lg truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{p.name}</h3>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span>{(p.items?.length || 0)} {t("projects.items")}</span>
                    <span className="font-black text-[#FF6600]" style={{ fontFamily: "Chivo, sans-serif" }}>&euro;{(p.total_cost || 0).toFixed(2)}</span>
                    <span>{formatDate(p.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`load-project-${p.id}`} onClick={() => loadProject(p)}
                    className="rmd-btn-primary text-xs px-4 py-2">
                    <RotateCcw size={14} /> {lang === "ru" ? "Загрузить" : lang === "en" ? "Load" : "Ielādēt"}
                  </button>
                  <button data-testid={`delete-project-${p.id}`} onClick={() => deleteProject(p.id)}
                    className="rmd-btn-secondary text-xs px-4 py-2 hover:bg-red-600 hover:text-white hover:border-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <footer className="bg-black py-8 mt-4">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white/30 text-xs">&copy; {new Date().getFullYear()} RMD – Real Men Do &middot; Cēsis, Latvija</p>
        </div>
      </footer>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => setShowAuth(false)} />}
    </div>
  );
}
